import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import { useEffect, useState } from "react";
import ReactInputMask from "react-input-mask";
import { useHistory } from "react-router-dom";
import { useGlobal } from "../../contexts/globalContext";
import CivitatisExperienceRepository from "../../repositories/civitatisExperienceRepository";
import getTermByTag from "../../repositories/termRepository";
import { getCep } from "../../services/via-cep.datasource";
import createCard64 from "../../utils/createCard64";
import cardFlag from "../../utils/getFlagCard";
import { getSessionStorage } from "../../utils/getSessionStorage";
import SimpleModal from "../SimpleModal";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#F5F7FA",
    minHeight: "100vh",
    paddingBottom: 60,
    paddingTop: 24,
  },
  header: {
    paddingBottom: 24,
    borderBottom: "1px solid #EAECEF",
    marginBottom: 32,
  },
  breadcrumb: {
    fontSize: 14,
    color: "#6B7280",
    cursor: "pointer",
    marginBottom: 16,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    "&:hover": { color: theme.palette.primary.main },
  },
  pageTitle: {
    fontSize: 32,
    fontWeight: 600,
    color: theme.palette.primary.main,
    margin: 0,
  },

  // Card sections
  card: {
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EAECEF",
    padding: 32,
    marginBottom: 24,
  },
  section: {
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EAECEF",
    padding: 24,
    marginBottom: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#2F3747",
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  sectionIcon: { fontSize: 20, color: theme.palette.primary.main },
  couponRow: { display: "flex", gap: 8, alignItems: "flex-end" },
  discountText: {
    color: "#008000",
    fontSize: 13,
    fontWeight: 600,
    marginTop: 4,
  },
  cancelGreen: { fontSize: 12, color: "#008000", fontWeight: 600 },

  // Form fields
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#9CA3AF",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 8,
    display: "block",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 24,
  },
  fieldRowFull: { gridColumn: "1 / -1", marginBottom: 24 },

  textField: {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: 6,
      fontSize: 14,
      backgroundColor: "#F8FAFC",
      border: "1px solid #E5E7EB",
      "& fieldset": { borderColor: "#E5E7EB" },
      "&:hover fieldset": { borderColor: "#D1D5DB" },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 12px",
      fontSize: 14,
      color: "#2F3747",
    },
  },

  selectField: {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: 6,
      fontSize: 14,
      backgroundColor: "#F8FAFC",
      border: "1px solid #E5E7EB",
      padding: 0,
      "& fieldset": { borderColor: "#E5E7EB" },
      "&:hover fieldset": { borderColor: "#D1D5DB" },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px !important",
      height: "auto !important",
      fontSize: 14,
      color: "#2F3747",
      lineHeight: 1.5,
    },
  },

  // Premium Sidebar (Référence-style)
  sidebar: {
    background: "#FFFFFF",
    borderRadius: 16,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    position: "sticky",
    top: 100,
    height: "fit-content",
  },
  sidebarImage: {
    width: "100%",
    height: 200,
    objectFit: "cover",
    borderRadius: "16px 16px 0 0",
  },
  sidebarContent: { padding: 24 },
  sidebarTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1F2937",
    marginBottom: 8,
  },
  sidebarDate: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 20,
    marginTop: 4,
  },
  sidebarDateIcon: { fontSize: 16 },
  divider: { height: 1, backgroundColor: "#E5E7EB", margin: "16px 0" },
  participantsSection: { marginBottom: 20 },
  participantRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 10,
    lineHeight: 1.6,
  },
  totalSection: {
    marginTop: 20,
    marginBottom: 20,
    paddingTop: 16,
    borderTop: "1px solid #E5E7EB",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  alertBox: {
    backgroundColor: "#FEF2F2",
    borderLeft: "4px solid #DC2626",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    fontSize: 13,
    color: "#7F1D1D",
  },
  alertIcon: { fontSize: 18, marginRight: 8, verticalAlign: "middle" },
  alertTitle: {
    fontWeight: 600,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  alertText: { lineHeight: 1.6, marginLeft: 26 },
  termsCheckbox: {
    marginBottom: 20,
    fontSize: 12,
    color: "#6B7280",
    lineHeight: 1.6,
  },
  termsLink: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  submitBtn: {
    width: "100%",
    height: 56,
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 16,
    background: theme.palette.primary.main,
    color: "#FFFFFF",
    "&:hover": {
      background: theme.palette.primary.dark || theme.palette.primary.main,
      boxShadow: `0 8px 24px ${theme.palette.primary.main}33`,
    },
    "&:disabled": {
      backgroundColor: "#D1D5DB",
      color: "#9CA3AF",
    },
  },
  securityBadge: {
    fontSize: 11,
    color: "#9CA3AF",
    textAlign: "center",
    letterSpacing: 0.5,
    fontWeight: 500,
    textTransform: "uppercase",
  },

  cardPreviewContainer: {
    perspective: "1000px",
    marginBottom: 24,
    display: "flex",
    justifyContent: "center",
  },
  cardPreview: {
    width: "100%",
    maxWidth: 320,
    height: 200,
    borderRadius: 12,
    padding: 24,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
    fontFamily: "monospace",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    transformStyle: "preserve-3d",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        "url('data:image/svg+xml,%3Csvg%20width=%22100%22%20height=%22100%22%20xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath%20d=%22M0%200h100v100H0z%22%20fill=%22%23fff%22%20opacity=%22.05%22/%3E%3C/svg%3E')",
      pointerEvents: "none",
    },
  },
  cardChip: {
    width: 50,
    height: 40,
    background: "linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)",
    borderRadius: 4,
    marginBottom: 20,
  },
  cardNumber: {
    fontSize: 20,
    letterSpacing: 3,
    fontWeight: 600,
    marginBottom: 16,
    minHeight: 24,
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  cardHolder: {
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 1,
    minHeight: 18,
  },
  cardExpiry: { fontSize: 13, minHeight: 18 },
  cardBrand: {
    position: "absolute",
    top: 16,
    right: 24,
    fontSize: 18,
    fontWeight: 700,
    opacity: 0.3,
  },

  // 3D card flip
  cardScene: {
    perspective: 1000,
    width: "100%",
    maxWidth: 340,
    height: 196,
    margin: "0 auto 24px",
  },
  cardInner: {
    position: "relative",
    width: "100%",
    height: "100%",
    transformStyle: "preserve-3d",
    transition: "transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  cardFlipped: {
    transform: "rotateY(180deg)",
  },
  cardFace: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backfaceVisibility: "hidden",
    borderRadius: 14,
    overflow: "hidden",
  },
  cardFront: {
    background:
      "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    color: "#fff",
    padding: "22px 24px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
  },
  cardBack: {
    background: "linear-gradient(135deg, #16213e 0%, #1a1a2e 100%)",
    transform: "rotateY(180deg)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
  },
}));

const civitatisRepo = new CivitatisExperienceRepository();

const CARD_FLAG = (number) => {
  if (/^4/.test(number)) return "Visa";
  if (/^5[1-5]/.test(number)) return "Mastercard";
  if (/^3[47]/.test(number)) return "Amex";
  return "Outros";
};

// Build Civitatis rate from a session/activity object.
// Category id=0 = adults, id=1 = seniors (treated as adults), id=2/3 = children.
// Civitatis rejects categories with quantity=0, so we filter them out.
// Group activities (isGroup): paxCounts holds people per group, but one service is booked per group.
const buildRate = (session, adults, childCount, paxCounts, isGroup = false) => {
  if (session?.rates?.length) {
    const r = session.rates[0];
    if (r.categories?.length) {
      const cats = r.categories
        .map((cat, i) => {
          const qty =
            paxCounts != null
              ? paxCounts[cat.id] ?? paxCounts[String(cat.id)] ?? 0
              : i === 0
              ? adults
              : i === 2
              ? childCount
              : 0;
          return { id: cat.id, quantity: isGroup ? Math.min(qty, 1) : qty };
        })
        .filter((c) => c.quantity > 0);
      return {
        id: r.id,
        categories:
          cats.length > 0 ? cats : [{ id: 0, quantity: adults + childCount }],
      };
    }
    return { id: r.id, categories: [{ id: 0, quantity: adults + childCount }] };
  }
  return {
    id: session?.id ?? 0,
    categories: [{ id: 0, quantity: adults + childCount }],
  };
};

const MAX_INSTALLMENTS = parseInt(
  process.env.REACT_APP_MAX_INSTALLMENTS || "6",
  10,
);

const EntertainmentCheckout = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useGlobal();
  const userPointsBalance = user?.pointsBalance || 0;

  const availability =
    getSessionStorage("@EntertainmentAvailabilitySelected") || {};
  const {
    date,
    session,
    paxCounts: rawPaxCounts,
    adults = 1,
    children: storedChildren = 0,
    childrenUnder2 = 0,
    children3to13 = 0,
    activity,
    activityId,
    refToken,
    totalPoints: storedTotalPoints,
    cartId: existingCartId,
    preAnswers,
  } = availability;

  const hasPassengersQuestions = !!activity?.hasPassengersQuestions;

  // Normalize paxCounts keys to numbers (JSON.parse gives string keys)
  const paxCounts = rawPaxCounts
    ? Object.fromEntries(
        Object.entries(rawPaxCounts).map(([k, v]) => [Number(k), v]),
      )
    : null;

  const childCount = storedChildren || childrenUnder2 + children3to13;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const [year, month, day] = dateStr.split("-");
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };
  const formattedDate = formatDate(date);

  const isGroupActivity = !!activity?.isCategoryPaxGroup;

  const paxCategories =
    activity?.rates?.[0]?.categories || session?.rates?.[0]?.categories || [];

  // For isCategoryPaxGroup: qty = people in the group (capped at group.maximum on the detail page)
  const groupPassengerConfig = (() => {
    if (!isGroupActivity) return null;
    const rateCategories = session?.rates?.[0]?.categories || [];
    let totalSlots = 0;
    let requiredSlots = 0;
    for (const cat of rateCategories) {
      const qty = paxCounts?.[cat.id] ?? paxCounts?.[String(cat.id)] ?? 0;
      if (qty === 0) continue;
      totalSlots += qty;
      requiredSlots += Math.min(qty, cat.group?.minimum ?? 1);
    }
    return totalSlots > 0 ? { totalSlots, requiredSlots } : null;
  })();

  const passengerTypes = (() => {
    // Group pax: one passenger slot per selected person
    if (isGroupActivity && groupPassengerConfig) {
      const rateCategories = session?.rates?.[0]?.categories || [];
      const types = [];
      for (const cat of rateCategories) {
        const qty = paxCounts?.[cat.id] ?? paxCounts?.[String(cat.id)] ?? 0;
        for (let p = 0; p < qty; p++)
          types.push(cat.name || cat.text || "Participante");
      }
      if (types.length) return types;
    }
    if (paxCounts && paxCategories.length) {
      const types = [];
      for (const cat of paxCategories) {
        const qty = paxCounts[cat.id] ?? paxCounts[String(cat.id)] ?? 0;
        for (let i = 0; i < qty; i++)
          types.push(cat.name || cat.text || "Participante");
      }
      if (types.length) return types;
    }
    // Fallback: original adults/children split
    return [
      ...Array(adults).fill("Adulto"),
      ...Array(childCount).fill("Criança"),
    ];
  })();

  const totalPax = passengerTypes.length || adults + childCount;
  const unitPointsPrice = activity?.pointsPrice ?? session?.pointsPrice ?? null;
  const unitCashPrice = parseFloat(activity?.price ?? session?.price ?? 0);

  // Group activities: use the per-category total already computed on the detail page
  const totalPoints = isGroupActivity && storedTotalPoints != null
    ? storedTotalPoints
    : (unitPointsPrice != null ? unitPointsPrice * totalPax : null);
  // Group activities: fixed price per service, so scale cash by points instead of multiplying by pax
  const totalBrl = !unitCashPrice
    ? null
    : isGroupActivity && totalPoints && unitPointsPrice
    ? unitCashPrice * (totalPoints / unitPointsPrice)
    : unitCashPrice * totalPax;

  // Passenger forms
  const buildPassenger = () => ({
    name: "",
    surname: "",
    document: "",
    birthDate: "",
    age: "",
    email: "",
    phone: "",
    gender: "",
    documentType: "CPF",
    cep: "",
    address: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
  });
  const [passengers, setPassengers] = useState(() => {
    const count = groupPassengerConfig?.totalSlots ?? (adults + childCount);
    return Array.from({ length: count }, (_, i) => {
      const pre = preAnswers?.passengers?.[i];
      if (!pre) return buildPassenger();
      return {
        ...buildPassenger(),
        name: pre.name || "",
        surname: pre.surname || "",
        document: pre.document || "",
        passport: pre.passport || "",
        passportExpiry: pre.passportExpiry || "",
        birthDate: pre.birthDate || "",
        email: pre.email || "",
        phone: pre.phone || "",
        age: pre.age || (pre.birthDate ? String(Math.max(0, Math.floor((Date.now() - new Date(pre.birthDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000)))) : ""),
        nationality: pre.nationality || "",
        extras: pre.extras || {},
      };
    });
  });

  const [cardHolder, setCardHolder] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiration, setCardExpiration] = useState("");
  const [cardCCV, setCardCCV] = useState("");
  const [installments, setInstallments] = useState(1);
  const [cvvFocused, setCvvFocused] = useState(false);

  const [coupon, setCoupon] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [validatingCard, setValidatingCard] = useState(false);
  const [cardValidationError, setCardValidationError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(() =>
    Math.min(userPointsBalance, Math.round(totalPoints || 0)),
  );
  const [paymentMethod, setPaymentMethod] = useState(() =>
    userPointsBalance > 0 ? "points" : "card",
  );

  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [openCancellationModal, setOpenCancellationModal] = useState(false);
  const [useTerms, setUseTerms] = useState(null);

  useEffect(() => {
    getTermByTag("USE_TERMS_PRIVACY_POLICY")
      .then((res) => setUseTerms(res?.data))
      .catch(() => {});
  }, []);

  const discountPoints = couponResult?.success
    ? couponResult?.data?.discountPoints || 0
    : 0;
  const discountCash = couponResult?.success
    ? couponResult?.data?.discountCash || 0
    : 0;

  const brlPerPoint = totalPoints > 0 ? (totalBrl || 0) / totalPoints : 0;
  const remainingBRL = Math.max(
    0,
    Math.round(
      ((totalBrl || 0) - discountCash - pointsToUse * brlPerPoint) * 100,
    ) / 100,
  );
  const needsCard = paymentMethod === "points" && remainingBRL > 0;
  const effectivePoints = Math.max(0, pointsToUse - discountPoints);

  // Auto-validate card when CVV is complete
  useEffect(() => {
    if (cardCCV.length >= 3 && cardNumber && cardHolder && cardExpiration) {
      validateCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardCCV]);

  const validateCard = async () => {
    if (
      cardNumber.length < 13 ||
      cardExpiration.length < 5 ||
      cardCCV.length < 3
    ) {
      return;
    }
    setValidatingCard(true);
    setCardValidationError("");
    try {
      const response = await civitatisRepo.validateCreditCard({
        cardNumber: cardNumber.replace(/\D/g, ""),
        cardBrand: cardFlag.getCardFlag(cardNumber) || CARD_FLAG(cardNumber),
        cardType: "CreditCard",
        holder: cardHolder,
        cardExpirationDate: cardExpiration,
        cardSecurityCode: cardCCV,
      });

      if (!response?.data?.valid) {
        setCardValidationError(
          "Cartão inválido, confira os dados e tente novamente.",
        );
      }
    } catch (error) {
      setCardValidationError(
        error.response?.data?.error?.message || "Erro ao validar cartão",
      );
    } finally {
      setValidatingCard(false);
    }
  };

  const updatePassenger = (idx, field, value) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const toUpperName = (value) =>
    value
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-zA-Z ]/g, "")
      .toUpperCase();

  const calculateAge = (birthDate) => {
    if (!birthDate) return 0;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return Math.max(0, age);
  };

  const handleCepChange = async (idx, maskedValue) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], cep: maskedValue };
      return next;
    });
    const digits = maskedValue.replace(/\D/g, "");
    if (digits.length === 8) {
      try {
        const { data } = await getCep(digits);
        if (data && !data.erro) {
          setPassengers((prev) => {
            const next = [...prev];
            next[idx] = {
              ...next[idx],
              address: data.logradouro || "",
              bairro: data.bairro || "",
              cidade: data.localidade || "",
              estado: data.uf || "",
            };
            return next;
          });
        }
      } catch {
        // ignore CEP lookup errors
      }
    }
  };

  const handleCheckCoupon = async () => {
    if (!coupon) return;
    setCouponLoading(true);
    const result = await civitatisRepo.checkDiscount({
      activityId,
      refToken,
      code: coupon,
      cashPrice: unitCashPrice || 0,
      cashTotalValue: totalBrl || 0,
      pointsTotalValue: Math.round(totalPoints || 0),
    });
    setCouponResult(result);
    setCouponLoading(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Map passengers to backend shape; for group activities, drop unfilled optional slots
      const mappedPassengers = passengers
        .filter((pax, idx) => idx < requiredPassengerCount || pax.name.trim())
        .map((pax, idx) => ({
          firstName: pax.name,
          lastName: pax.surname,
          birthDate: pax.birthDate || null,
          document: {
            number: (pax.document || "").replace(/\D/g, ""),
            type: pax.documentType || "CPF",
          },
          gender: pax.gender || "",
          phone: pax.phone || "",
          email: pax.email || "",
          isMain: idx === 0,
          passport: pax.passport || "",
          passportExpiry: pax.passportExpiry || "",
          nationality: pax.nationality || "",
          extras: pax.extras || {},
        }));

      const activityImage =
        activity?.photos?.gallery?.[0]?.paths?.original ||
        activity?.photos?.header?.[0]?.paths?.original ||
        activity?.image ||
        null;

      // Booking-level question answers collected in the pre-checkout form (if any)
      const bookingAnswers = preAnswers?.bookingAnswers?.length
        ? { bookingAnswers: preAnswers.bookingAnswers }
        : {};

      const serviceData = {
        activityId,
        activityName:
          session?.name || session?.title || activity?.title || activity?.name,
        activityDate: date,
        activityTime: session?.time || null,
        rate: buildRate(session, adults, childCount, paxCounts, isGroupActivity),
        passengers: mappedPassengers,
        existingCartId: existingCartId ?? null,
        refToken,
        activityImage,
        ...bookingAnswers,
      };

      // v1: use single unit price as totals (single-pax validated against refToken)
      const cashUnit = parseFloat(unitCashPrice || 0);
      const pointsUnit = parseInt(unitPointsPrice || 0, 10);

      // customer object — needed for card or mixed payment
      const mainPax = passengers[0] || {};
      const customer = {
        firstName: mainPax.name || "",
        lastName: mainPax.surname || "",
        documentNumber: (mainPax.document || "").replace(/\D/g, ""),
        documentType: mainPax.documentType || "CPF",
        email: mainPax.email || user?.email || "",
        zipCode: (mainPax.cep || "").replace(/\D/g, ""),
        address: mainPax.address || "",
        number: mainPax.numero || "",
        complement: mainPax.complemento || "",
        city: mainPax.cidade || "",
        state: mainPax.estado || "",
        ipAddress: "",
      };

      let model;

      if (paymentMethod === "points" && remainingBRL === 0) {
        // Full points payment
        model = {
          cashPrice: cashUnit,
          pointsPrice: pointsUnit,
          pointsTotalValue: effectivePoints,
          cashTotalValue: "0.00",
          discountPoints,
          discountCash,
          coupon: couponResult?.success ? coupon : null,
          services: [{ type: "civitatis-experience", data: serviceData }],
        };
      } else if (paymentMethod === "points" && remainingBRL > 0) {
        // Mixed: partial points + card for the remainder
        model = {
          cashPrice: cashUnit,
          pointsPrice: pointsUnit,
          pointsTotalValue: effectivePoints,
          cashTotalValue: remainingBRL.toFixed(2),
          discountPoints,
          discountCash,
          coupon: couponResult?.success ? coupon : null,
          services: [{ type: "civitatis-experience", data: serviceData }],
          payment: {
            customer,
            cardInfo: createCard64(
              cardHolder,
              cardNumber,
              cardExpiration,
              cardCCV,
            ),
            installments: parseInt(installments, 10),
            saveCreditCard: false,
          },
        };
      } else {
        // Full card payment
        const effectiveCash = Math.max(0, cashUnit - discountCash);
        model = {
          cashPrice: cashUnit,
          pointsPrice: pointsUnit,
          pointsTotalValue: 0,
          cashTotalValue: effectiveCash.toFixed(2),
          discountPoints,
          discountCash,
          coupon: couponResult?.success ? coupon : null,
          services: [{ type: "civitatis-experience", data: serviceData }],
          payment: {
            customer,
            cardInfo: createCard64(
              cardHolder,
              cardNumber,
              cardExpiration,
              cardCCV,
            ),
            installments: parseInt(installments, 10),
            saveCreditCard: false,
          },
        };
      }

      const result = await civitatisRepo.booking({ data: model });

      const bookingData =
        result?.data?.bookingResponse ?? result?.data ?? result;
      const bookingCode = bookingData?.bookingCode;
      if (bookingCode) {
        sessionStorage.setItem("@EntertainmentBookingCode", bookingCode);
        sessionStorage.setItem(
          "@EntertainmentBookingResult",
          JSON.stringify(bookingData),
        );
        sessionStorage.setItem(
          "@EntertainmentPaymentSummary",
          JSON.stringify({
            pointsTotalValue: model.pointsTotalValue,
            cashTotalValue: parseFloat(model.cashTotalValue || "0"),
          }),
        );
        history.push("/busca/entretenimento/confirmado");
      } else {
        const errorMsg =
          result?.error ||
          result?.message ||
          "Não foi possível concluir a reserva. Tente novamente.";
        sessionStorage.setItem("@EntertainmentBookingError", errorMsg);
        history.push("/busca/entretenimento/erro");
      }
    } catch (error) {
      console.error("Erro no checkout:", error);
      const errorMsg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao processar reserva.";
      sessionStorage.setItem("@EntertainmentBookingError", errorMsg);
      history.push("/busca/entretenimento/erro");
    } finally {
      setSubmitting(false);
    }
  };

  const isCardComplete =
    cardHolder &&
    cardNumber &&
    cardExpiration &&
    cardCCV &&
    !cardValidationError &&
    passengers[0]?.document &&
    passengers[0]?.address;

  // For group activities only required slots (group.minimum) must be filled; optional slots can be empty
  const requiredPassengerCount = groupPassengerConfig?.requiredSlots ?? passengers.length;
  const allPassengersValid = passengers.slice(0, requiredPassengerCount).every(
    (pax) =>
      pax.name.trim() &&
      pax.surname.trim() &&
      (!hasPassengersQuestions || (pax.age && parseInt(pax.age, 10) > 0)),
  );

  const canSubmit =
    acceptedTerms &&
    !submitting &&
    allPassengersValid &&
    (paymentMethod === "points" && !needsCard ? true : isCardComplete);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Container maxWidth="lg">
          <span className={classes.breadcrumb} onClick={() => history.goBack()}>
            <ArrowBackIcon style={{ fontSize: 16 }} />
            Voltar
          </span>
          <Typography className={classes.pageTitle}>
            Finalizar Reserva
          </Typography>
        </Container>
      </div>

      <Container maxWidth="lg" style={{ paddingTop: 0 }}>
        <Grid container spacing={3}>
          {/* ── Left column ── */}
          <Grid item xs={12} md={8}>
            {/* Passenger forms */}
            {passengers.map((pax, idx) => {
              const isOptionalPassenger = idx >= requiredPassengerCount;
              // Fields answered in the pre-checkout questions form are hidden here
              const prePax = preAnswers?.passengers?.[idx];
              return (
              <div key={idx} className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  <PersonIcon className={classes.sectionIcon} />
                  {(() => {
                    const type = `${passengerTypes[idx] || "Participante"}${isOptionalPassenger ? " - Opcional" : ""}`;
                    const fullName = prePax?.name
                      ? `${prePax.name}${prePax.surname ? ` ${prePax.surname}` : ""}`.trim()
                      : null;
                    return fullName
                      ? `Passageiro ${idx + 1} — ${fullName} (${type})`
                      : `Dados do Passageiro ${idx + 1} (${type})`;
                  })()}
                </Typography>
                <Grid container spacing={2}>
                  {!prePax?.name && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nome *"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex. JOÃO"
                      value={pax.name}
                      onChange={(e) =>
                        updatePassenger(idx, "name", e.target.value)
                      }
                      onBlur={(e) =>
                        updatePassenger(
                          idx,
                          "name",
                          toUpperName(e.target.value),
                        )
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  )}
                  {!prePax?.surname && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Sobrenome *"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex. SILVA"
                      value={pax.surname}
                      onChange={(e) =>
                        updatePassenger(idx, "surname", e.target.value)
                      }
                      onBlur={(e) =>
                        updatePassenger(
                          idx,
                          "surname",
                          toUpperName(e.target.value),
                        )
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  )}
                  {!prePax?.birthDate && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Data de Nascimento"
                      variant="outlined"
                      fullWidth
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={pax.birthDate}
                      onChange={(e) =>
                        updatePassenger(idx, "birthDate", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  )}
                  {hasPassengersQuestions && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Idade *"
                        variant="outlined"
                        fullWidth
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, max: 120 }}
                        placeholder="Ex. 30"
                        value={pax.age}
                        onChange={(e) =>
                          updatePassenger(idx, "age", e.target.value)
                        }
                        className={classes.textField}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Gênero"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ displayEmpty: true }}
                      value={pax.gender}
                      onChange={(e) =>
                        updatePassenger(idx, "gender", e.target.value)
                      }
                      className={classes.selectField}
                    >
                      <MenuItem value="">Selecione</MenuItem>
                      <MenuItem value="M">Masculino</MenuItem>
                      <MenuItem value="F">Feminino</MenuItem>
                      <MenuItem value="O">Outro</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Tipo de Documento"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={pax.documentType}
                      onChange={(e) =>
                        updatePassenger(idx, "documentType", e.target.value)
                      }
                      className={classes.selectField}
                    >
                      <MenuItem value="CPF">CPF</MenuItem>
                      <MenuItem value="RG">RG</MenuItem>
                      <MenuItem value="Passport">Passaporte</MenuItem>
                    </TextField>
                  </Grid>
                  {!prePax?.document && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Número do Documento"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="000.000.000-00"
                      value={pax.document}
                      onChange={(e) =>
                        updatePassenger(idx, "document", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  )}
                  {!prePax?.email && (
                  <Grid item xs={12}>
                    <TextField
                      label="E-mail de Contato"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="email"
                      placeholder="seu@email.com"
                      value={pax.email}
                      onChange={(e) =>
                        updatePassenger(idx, "email", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  )}
                  {!prePax?.phone && (
                  <Grid item xs={12} sm={6}>
                    <ReactInputMask
                      mask="(99) 99999-9999"
                      value={pax.phone}
                      onChange={(e) =>
                        updatePassenger(idx, "phone", e.target.value)
                      }
                    >
                      {(inputProps) => (
                        <TextField
                          {...inputProps}
                          label="Telefone / WhatsApp"
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          className={classes.textField}
                        />
                      )}
                    </ReactInputMask>
                  </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <ReactInputMask
                      mask="99999-999"
                      value={pax.cep}
                      onChange={(e) => handleCepChange(idx, e.target.value)}
                    >
                      {(inputProps) => (
                        <TextField
                          {...inputProps}
                          label="CEP"
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          className={classes.textField}
                        />
                      )}
                    </ReactInputMask>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Rua / Logradouro"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={pax.address}
                      onChange={(e) =>
                        updatePassenger(idx, "address", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Número"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={pax.numero}
                      onChange={(e) =>
                        updatePassenger(idx, "numero", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Complemento"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={pax.complemento}
                      onChange={(e) =>
                        updatePassenger(idx, "complemento", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bairro"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={pax.bairro}
                      onChange={(e) =>
                        updatePassenger(idx, "bairro", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Cidade"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={pax.cidade}
                      onChange={(e) =>
                        updatePassenger(idx, "cidade", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="UF"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={pax.estado}
                      onChange={(e) =>
                        updatePassenger(idx, "estado", e.target.value)
                      }
                      className={classes.textField}
                    />
                  </Grid>
                </Grid>
              </div>
            );
            })}

            {/* ── Payment method + card data ── */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <CreditCardIcon className={classes.sectionIcon} />
                Forma de Pagamento
              </Typography>

              <Grid container spacing={2} style={{ marginBottom: 20 }}>
                {[
                  {
                    value: "card",
                    label: "Cartão de crédito",
                    sub:
                      pointsToUse > 0 && remainingBRL < (totalBrl || 0)
                        ? `R$ ${remainingBRL
                            .toFixed(2)
                            .replace(".", ",")} (após pontos)`
                        : `Até ${MAX_INSTALLMENTS}x sem juros`,
                    icon: <CreditCardIcon style={{ fontSize: 20 }} />,
                  },
                  {
                    value: "points",
                    label: "Saldo de Pontos",
                    sub: `Saldo: ${userPointsBalance.toLocaleString(
                      "pt-BR",
                    )} pts`,
                    icon: <CheckCircleOutlineIcon style={{ fontSize: 20 }} />,
                  },
                ].map((opt) => (
                  <Grid item xs={6} key={opt.value}>
                    <div
                      onClick={() => setPaymentMethod(opt.value)}
                      style={{
                        border: `2px solid ${
                          paymentMethod === opt.value ? "#c0392b" : "#E5E7EB"
                        }`,
                        borderRadius: 8,
                        padding: "12px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        backgroundColor:
                          paymentMethod === opt.value ? "#fff5f5" : "#fff",
                      }}
                    >
                      <Radio
                        checked={paymentMethod === opt.value}
                        color="primary"
                        size="small"
                        style={{
                          padding: 0,
                          color:
                            paymentMethod === opt.value ? "#c0392b" : undefined,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: "#333",
                          }}
                        >
                          {opt.label}
                        </div>
                        <div style={{ fontSize: 11, color: "#999" }}>
                          {opt.sub}
                        </div>
                      </div>
                      <span
                        style={{
                          color:
                            paymentMethod === opt.value ? "#c0392b" : "#ccc",
                        }}
                      >
                        {opt.icon}
                      </span>
                    </div>
                  </Grid>
                ))}
              </Grid>

              {paymentMethod === "points" && totalPoints != null && (
                <Grid container spacing={2} style={{ marginBottom: 20 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Pontos a utilizar"
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={pointsToUse}
                      onChange={(e) => {
                        const max = Math.min(
                          userPointsBalance,
                          Math.round(totalPoints),
                        );
                        const val = Math.max(
                          0,
                          Math.min(parseInt(e.target.value, 10) || 0, max),
                        );
                        setPointsToUse(val);
                      }}
                      inputProps={{
                        min: 0,
                        max: Math.min(
                          userPointsBalance,
                          Math.round(totalPoints),
                        ),
                        step: 1,
                      }}
                      helperText={
                        pointsToUse > userPointsBalance
                          ? "Saldo insuficiente de pontos"
                          : `Máximo: ${Math.min(
                              userPointsBalance,
                              Math.round(totalPoints),
                            ).toLocaleString("pt-BR")} pts`
                      }
                      error={pointsToUse > userPointsBalance}
                      className={classes.textField}
                    />
                  </Grid>
                  {needsCard && (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography style={{ fontSize: 13, color: "#555" }}>
                        Diferença no cartão:{" "}
                        <strong style={{ color: "#c0392b" }}>
                          R$ {remainingBRL.toFixed(2).replace(".", ",")}
                        </strong>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}

              {(paymentMethod === "card" || needsCard) && (
                <Grid container spacing={2}>
                  {/* ── 3D flip card preview ── */}
                  <Grid item xs={12}>
                    <div className={classes.cardScene}>
                      <div
                        className={`${classes.cardInner} ${
                          cvvFocused ? classes.cardFlipped : ""
                        }`}
                      >
                        {/* Front */}
                        <div
                          className={`${classes.cardFace} ${classes.cardFront}`}
                        >
                          {/* Top row: chip + brand */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            {/* EMV chip */}
                            <svg
                              width="36"
                              height="28"
                              viewBox="0 0 36 28"
                              fill="none"
                            >
                              <rect
                                width="36"
                                height="28"
                                rx="4"
                                fill="#d4a843"
                                opacity="0.9"
                              />
                              <rect
                                x="12"
                                y="0"
                                width="12"
                                height="28"
                                rx="0"
                                fill="#c49a30"
                                opacity="0.4"
                              />
                              <rect
                                x="0"
                                y="9"
                                width="36"
                                height="10"
                                rx="0"
                                fill="#c49a30"
                                opacity="0.4"
                              />
                              <rect
                                x="12"
                                y="9"
                                width="12"
                                height="10"
                                rx="2"
                                fill="#b8872a"
                                opacity="0.5"
                              />
                            </svg>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: 1,
                                opacity: 0.7,
                                textTransform: "uppercase",
                              }}
                            >
                              {cardFlag.getCardFlag(cardNumber) || ""}
                            </span>
                          </div>

                          {/* Card number */}
                          <div
                            style={{
                              fontFamily: "monospace",
                              fontSize: 18,
                              letterSpacing: 3,
                              fontWeight: 500,
                              marginTop: 8,
                            }}
                          >
                            {(cardNumber || "")
                              .replace(/\D/g, "")
                              .padEnd(16, "•")
                              .replace(/(.{4})/g, "$1 ")
                              .trim()}
                          </div>

                          {/* Bottom row: holder + expiry */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-end",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: 9,
                                  opacity: 0.5,
                                  textTransform: "uppercase",
                                  letterSpacing: 1,
                                  marginBottom: 2,
                                }}
                              >
                                Titular
                              </div>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  letterSpacing: 1,
                                  textTransform: "uppercase",
                                  maxWidth: 180,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {cardHolder || "SEU NOME"}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div
                                style={{
                                  fontSize: 9,
                                  opacity: 0.5,
                                  textTransform: "uppercase",
                                  letterSpacing: 1,
                                  marginBottom: 2,
                                }}
                              >
                                Validade
                              </div>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  fontFamily: "monospace",
                                }}
                              >
                                {cardExpiration || "MM/AA"}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Back */}
                        <div
                          className={`${classes.cardFace} ${classes.cardBack}`}
                        >
                          {/* Magnetic stripe */}
                          <div
                            style={{
                              height: 44,
                              background: "#111",
                              marginTop: 28,
                              width: "100%",
                            }}
                          />
                          {/* Signature + CVV strip */}
                          <div
                            style={{
                              padding: "14px 24px",
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                height: 36,
                                background:
                                  "repeating-linear-gradient(90deg, #e8e0d0 0px, #e8e0d0 4px, #d4c9b5 4px, #d4c9b5 8px)",
                                borderRadius: 4,
                              }}
                            />
                            <div
                              style={{
                                background: "#fff",
                                color: "#111",
                                fontFamily: "monospace",
                                fontWeight: 700,
                                fontSize: 16,
                                letterSpacing: 3,
                                padding: "6px 14px",
                                borderRadius: 4,
                                minWidth: 52,
                                textAlign: "center",
                              }}
                            >
                              {cardCCV || "•••"}
                            </div>
                          </div>
                          <div style={{ padding: "0 24px" }}>
                            <div
                              style={{
                                fontSize: 9,
                                color: "rgba(255,255,255,0.4)",
                                textTransform: "uppercase",
                                letterSpacing: 1,
                              }}
                            >
                              Código de segurança
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Número do Cartão"
                      variant="outlined"
                      fullWidth
                      value={cardNumber}
                      onChange={(e) =>
                        setCardNumber(
                          e.target.value.replace(/\D/g, "").slice(0, 16),
                        )
                      }
                      className={classes.textField}
                      InputProps={{
                        endAdornment: cardNumber ? (
                          <InputAdornment position="end">
                            <Typography style={{ fontSize: 12, color: "#777" }}>
                              {CARD_FLAG(cardNumber)}
                            </Typography>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nome no Cartão"
                      variant="outlined"
                      fullWidth
                      value={cardHolder}
                      onChange={(e) => setCardHolder(e.target.value)}
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="MM/AA"
                      variant="outlined"
                      fullWidth
                      value={cardExpiration}
                      onChange={(e) => setCardExpiration(e.target.value)}
                      inputProps={{ maxLength: 7 }}
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="CVV"
                      variant="outlined"
                      fullWidth
                      value={cardCCV}
                      onChange={(e) =>
                        setCardCCV(
                          e.target.value.replace(/\D/g, "").slice(0, 4),
                        )
                      }
                      onFocus={() => setCvvFocused(true)}
                      onBlur={() => setCvvFocused(false)}
                      inputProps={{ maxLength: 4 }}
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Select
                      value={installments}
                      onChange={(e) => setInstallments(e.target.value)}
                      fullWidth
                      variant="outlined"
                      className={classes.selectField}
                    >
                      {Array.from(
                        { length: MAX_INSTALLMENTS },
                        (_, i) => i + 1,
                      ).map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}x{" "}
                          {n === 1
                            ? `de R$ ${unitCashPrice
                                .toFixed(2)
                                .replace(".", ",")}`
                            : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  {validatingCard && (
                    <Grid item xs={12}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 12,
                          color: "#999",
                        }}
                      >
                        <CircularProgress size={14} /> Validando cartão…
                      </div>
                    </Grid>
                  )}
                  {cardValidationError && (
                    <Grid item xs={12}>
                      <Typography style={{ color: "#c00", fontSize: 13 }}>
                        {cardValidationError}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </div>

            {/* ── Coupon ── */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Cupom de desconto
              </Typography>
              <div className={classes.couponRow}>
                <TextField
                  label="Código do cupom"
                  variant="outlined"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  style={{ flexGrow: 1 }}
                  className={classes.textField}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCheckCoupon}
                  disabled={couponLoading || !coupon}
                  style={{ height: 44, minWidth: 100, marginLeft: 8 }}
                >
                  {couponLoading ? <CircularProgress size={20} /> : "Aplicar"}
                </Button>
              </div>
              {couponResult?.success && (
                <Typography className={classes.discountText}>
                  <CheckCircleOutlineIcon
                    style={{
                      fontSize: 16,
                      verticalAlign: "middle",
                      marginRight: 4,
                    }}
                  />
                  Desconto aplicado:{" "}
                  {couponResult.data?.discountPoints
                    ? `${couponResult.data.discountPoints} pts`
                    : couponResult.data?.discountCash
                    ? `R$ ${Number(couponResult.data.discountCash)
                        .toFixed(2)
                        .replace(".", ",")}`
                    : "Cupom válido"}
                </Typography>
              )}
              {couponResult && !couponResult.success && (
                <Typography
                  style={{ color: "#c00", fontSize: 13, marginTop: 4 }}
                >
                  {couponResult.error || "Cupom inválido ou expirado."}
                </Typography>
              )}
            </div>
          </Grid>

          {/* ── Right column — sticky sidebar ── */}
          <Grid item xs={12} md={4}>
            <div className={classes.sidebar}>
              {(activity?.photos?.gallery?.[0]?.paths?.original ||
                activity?.photos?.header?.[0]?.paths?.original ||
                activity?.image) && (
                <img
                  src={
                    activity?.photos?.gallery?.[0]?.paths?.original ||
                    activity?.photos?.header?.[0]?.paths?.original ||
                    activity?.image
                  }
                  alt={activity?.title || activity?.name}
                  className={classes.sidebarImage}
                />
              )}

              <div className={classes.sidebarContent}>
                <Typography className={classes.sidebarTitle}>
                  {activity?.title || activity?.name}
                </Typography>

                <div className={classes.sidebarDate}>
                  <EventIcon className={classes.sidebarDateIcon} />
                  {formattedDate} {session?.time ? `• ${session.time}` : ""}
                </div>

                <div className={classes.divider} />

                <div className={classes.participantsSection}>
                  {paxCategories.length && paxCounts
                    ? paxCategories
                        .filter(
                          (cat) =>
                            (paxCounts[cat.id] ??
                              paxCounts[String(cat.id)] ??
                              0) > 0,
                        )
                        .map((cat) => {
                          const qty =
                            paxCounts[cat.id] ?? paxCounts[String(cat.id)] ?? 0;
                          return (
                            <div
                              key={cat.id}
                              className={classes.participantRow}
                            >
                              <span>
                                {isGroupActivity
                                  ? `${cat.name || cat.text} (${qty} pessoa${qty !== 1 ? "s" : ""})`
                                  : `${qty}x ${cat.name || cat.text || "Participante"}`}
                              </span>
                              <span>
                                Pts{" "}
                                {Math.round(
                                  isGroupActivity
                                    ? session?.rates?.[0]?.categories?.find((c) => c.id === cat.id)?.pointsPrice ?? unitPointsPrice ?? 0
                                    : (unitPointsPrice || 0) * qty,
                                ).toLocaleString("pt-BR")}
                              </span>
                            </div>
                          );
                        })
                    : [
                        adults > 0 && (
                          <div key="adults" className={classes.participantRow}>
                            <span>
                              {adults}x Adulto{adults !== 1 ? "s" : ""}
                            </span>
                            <span>
                              Pts{" "}
                              {Math.round(
                                (unitPointsPrice || 0) * adults,
                              ).toLocaleString("pt-BR")}
                            </span>
                          </div>
                        ),
                        childCount > 0 && (
                          <div
                            key="children"
                            className={classes.participantRow}
                          >
                            <span>
                              {childCount}x Criança{childCount !== 1 ? "s" : ""}
                            </span>
                            <span>
                              Pts{" "}
                              {Math.round(
                                (unitPointsPrice || 0) * childCount,
                              ).toLocaleString("pt-BR")}
                            </span>
                          </div>
                        ),
                      ]}
                </div>

                <div className={classes.totalSection}>
                  {discountPoints > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        color: "#2e7d32",
                        marginBottom: 8,
                      }}
                    >
                      <span>Desconto cupom</span>
                      <span>
                        - Pts {discountPoints.toLocaleString("pt-BR")}
                      </span>
                    </div>
                  )}
                  {discountCash > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        color: "#2e7d32",
                        marginBottom: 8,
                      }}
                    >
                      <span>Desconto cupom</span>
                      <span>
                        - R$ {Number(discountCash).toFixed(2).replace(".", ",")}
                      </span>
                    </div>
                  )}
                  <div className={classes.totalRow}>
                    <span>TOTAL</span>
                    <div style={{ textAlign: "right" }}>
                      {paymentMethod === "points" ? (
                        needsCard ? (
                          <>
                            <div>
                              Pts{" "}
                              {Math.round(effectivePoints).toLocaleString(
                                "pt-BR",
                              )}
                            </div>
                            <div style={{ fontSize: 14, color: "#c0392b" }}>
                              + R$ {remainingBRL.toFixed(2).replace(".", ",")}
                            </div>
                          </>
                        ) : (
                          `Pts ${Math.round(effectivePoints).toLocaleString(
                            "pt-BR",
                          )}`
                        )
                      ) : unitCashPrice > 0 ? (
                        `R$ ${Math.max(0, unitCashPrice - discountCash)
                          .toFixed(2)
                          .replace(".", ",")}`
                      ) : (
                        `Pts ${Math.round(totalPoints || 0).toLocaleString(
                          "pt-BR",
                        )}`
                      )}
                    </div>
                  </div>
                </div>

                <div className={classes.termsCheckbox}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <span>
                        Eu li e aceito os{" "}
                        <a
                          href="#"
                          className={classes.termsLink}
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenTermsModal(true);
                          }}
                        >
                          termos e condições
                        </a>
                        , política de{" "}
                        <a
                          href="#"
                          className={classes.termsLink}
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenCancellationModal(true);
                          }}
                        >
                          cancelamento
                        </a>{" "}
                        e normas de segurança.
                      </span>
                    }
                  />
                </div>

                <Button
                  variant="contained"
                  className={classes.submitBtn}
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "CONFIRMAR RESERVA"
                  )}
                </Button>

                <Typography className={classes.securityBadge}>
                  🔒 Pagamento seguro
                </Typography>
              </div>
            </div>
          </Grid>
        </Grid>
      </Container>

      {openTermsModal && (
        <SimpleModal
          opened={openTermsModal}
          openedState={setOpenTermsModal}
          title={useTerms?.description || "Termos e Condições"}
          text={
            useTerms?.content ||
            "<p>Termos e condições não disponíveis no momento.</p>"
          }
          closeButtonText="Fechar"
          allowCloseByClickOutside={true}
          closeButtonDisabledTillScrollEnded={false}
        />
      )}

      {openCancellationModal && (
        <SimpleModal
          opened={openCancellationModal}
          openedState={setOpenCancellationModal}
          title="Política de Cancelamento"
          text={
            activity?.cancelPolicy
              ? activity.cancelPolicy
              : "<p>Política de cancelamento não disponível para esta atividade.</p>"
          }
          closeButtonText="Fechar"
          allowCloseByClickOutside={true}
          closeButtonDisabledTillScrollEnded={false}
        />
      )}
    </div>
  );
};

export default EntertainmentCheckout;
